import React, { useRef } from 'react';
import { DatePicker, Badge, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';

// Generate a Dayjs-based DatePicker
const DayjsDatePicker = DatePicker.generatePicker<Dayjs>(dayjsGenerateConfig);

interface DateTimePickerFieldProps {
  value?: string | Dayjs | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  format?: string;
  showTime?: boolean;
  disabled?: boolean;
  disabledDate?: (current: Dayjs) => boolean;
  style?: React.CSSProperties;
  size?: 'small' | 'middle' | 'large';
  allowClear?: boolean;
  className?: string;
  minuteStep?: number;
  showNow?: boolean;
  showToday?: boolean;
  showShortcuts?: boolean;
  [key: string]: any; // Allow additional props
}

export const DateTimePickerField: React.FC<DateTimePickerFieldProps> = ({
  value,
  onChange,
  placeholder = 'Select date and time',
  format = 'YYYY-MM-DD hh:mm A',
  showTime = true,
  disabled = false,
  disabledDate,
  style,
  size = 'middle',
  allowClear = true,
  className,
  minuteStep,
  showNow,
  showToday,
  showShortcuts = false,
  ...props
}) => {
  const [internalValue, setInternalValue] = React.useState<Dayjs | undefined>();
  
  // Parse the value to Dayjs object
  const getDayjsValue = (): Dayjs | undefined => {
    // Use internal value if available, otherwise parse external value
    if (internalValue) return internalValue;
    
    if (!value) return undefined;

    let dayjsDate: Dayjs;

    // If it's already a Dayjs object
    if (dayjs.isDayjs(value)) {
      dayjsDate = value;
    } else {
      // If it's a string, parse it
      dayjsDate = dayjs(value);
    }

    // Return Dayjs object if valid, otherwise undefined
    return dayjsDate.isValid() ? dayjsDate : undefined;
  };

  // Update internal value when external value changes
  React.useEffect(() => {
    if (value) {
      let dayjsDate: Dayjs;
      if (dayjs.isDayjs(value)) {
        dayjsDate = value;
      } else {
        dayjsDate = dayjs(value);
      }
      if (dayjsDate.isValid()) {
        setInternalValue(dayjsDate);
      }
    } else {
      setInternalValue(undefined);
    }
  }, [value]);

  const handleChange = (dayjsDate: Dayjs | null) => {
    // Update internal state immediately
    setInternalValue(dayjsDate || undefined);
    
    if (onChange) {
      onChange(dayjsDate ? dayjsDate.toISOString() : null);
    }
  };

  const defaultDisabledDate = (current: Dayjs) => {
    return current && current.isBefore(dayjs().startOf('year'));
  };

  // Helper function to round to nearest 15 or 30 minutes
  const roundToNearestInterval = (
    date: Dayjs,
    interval: number = 15
  ): Dayjs => {
    const minutes = date.minute();
    const roundedMinutes = Math.round(minutes / interval) * interval;
    return date.minute(roundedMinutes).second(0);
  };

  // Helper function to get next business day
  const getNextBusinessDay = (): Dayjs => {
    const tomorrow = dayjs().add(1, 'day');
    // If tomorrow is Sunday (0), add one more day to get Monday
    if (tomorrow.day() === 0) {
      return tomorrow.add(1, 'day');
    }
    return tomorrow;
  };

  // Quick set handlers
  const handleQuickSet = (type: string) => {
    let newDate: Dayjs;

    switch (type) {
      case 'tomorrow-10-30':
        newDate = getNextBusinessDay().hour(10).minute(30).second(0);
        break;
      case 'today-1hr':
        newDate = roundToNearestInterval(dayjs().add(1, 'hour'));
        break;
      case 'today-2hr':
        newDate = roundToNearestInterval(dayjs().add(2, 'hours'));
        break;
      case 'today-5hr':
        newDate = roundToNearestInterval(dayjs().add(5, 'hours'));
        break;
      case '20-days':
        newDate = dayjs().add(20, 'days').hour(10).minute(0).second(0);
        // If it's Sunday (0), move to Monday
        if (newDate.day() === 0) {
          newDate = newDate.add(1, 'day');
        }
        break;
      case '1-month':
        newDate = dayjs().add(1, 'month').hour(10).minute(0).second(0);
        // If it's Sunday (0), move to Monday
        if (newDate.day() === 0) {
          newDate = newDate.add(1, 'day');
        }
        break;
      default:
        return;
    }

    if (onChange) {
      onChange(newDate.toISOString());
    }
  };

  const datePickerProps: any = {
    value: getDayjsValue(),
    onChange: handleChange,
    placeholder,
    format: showTime ? format || 'YYYY-MM-DD hh:mm A' : format || 'YYYY-MM-DD',
    showTime,
    disabled,
    disabledDate: disabledDate || defaultDisabledDate,

    style: { width: '100%', ...style },
    size,
    allowClear,
    className,
    showNow,
    showToday,
    use12Hours: showTime ? true : undefined,
    ...props,
  };

  // Only add minuteStep if it's provided and showTime is true
  if (showTime && minuteStep) {
    datePickerProps.minuteStep = minuteStep;
  }

  return (
    <div>
      <DayjsDatePicker {...datePickerProps} />
      {showTime && showShortcuts && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <Badge
              count={`${
                getNextBusinessDay().day() === 1 ? 'Monday' : 'Tomorrow'
              } 10:30 AM`}
              style={{
                cursor: 'pointer',
                backgroundColor: 'rgba(135, 206, 250, 0.2)',
                color: '#1890ff',
                border: '1px solid rgba(24, 144, 255, 0.3)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.2',
                flexShrink: 0,
              }}
              onClick={() => handleQuickSet('tomorrow-10-30')}
            />
            <Badge
              count={roundToNearestInterval(dayjs().add(1, 'hour')).format(
                'h:mm A'
              )}
              style={{
                cursor: 'pointer',
                backgroundColor: 'rgba(144, 238, 144, 0.2)',
                color: '#52c41a',
                border: '1px solid rgba(82, 196, 26, 0.3)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.2',
                flexShrink: 0,
              }}
              onClick={() => handleQuickSet('today-1hr')}
            />
            <Badge
              count={roundToNearestInterval(dayjs().add(2, 'hours')).format(
                'h:mm A'
              )}
              style={{
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 182, 193, 0.2)',
                color: '#eb2f96',
                border: '1px solid rgba(235, 47, 150, 0.3)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.2',
                flexShrink: 0,
              }}
              onClick={() => handleQuickSet('today-2hr')}
            />
            <Badge
              count={roundToNearestInterval(dayjs().add(5, 'hours')).format(
                'h:mm A'
              )}
              style={{
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 218, 185, 0.2)',
                color: '#fa8c16',
                border: '1px solid rgba(250, 140, 22, 0.3)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.2',
                flexShrink: 0,
              }}
              onClick={() => handleQuickSet('today-5hr')}
            />
            <Badge
              count="20 Days"
              style={{
                cursor: 'pointer',
                backgroundColor: 'rgba(138, 43, 226, 0.2)',
                color: '#8a2be2',
                border: '1px solid rgba(138, 43, 226, 0.3)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.2',
                flexShrink: 0,
              }}
              onClick={() => handleQuickSet('20-days')}
            />
            <Badge
              count="1 Month"
              style={{
                cursor: 'pointer',
                backgroundColor: 'rgba(75, 0, 130, 0.2)',
                color: '#4b0082',
                border: '1px solid rgba(75, 0, 130, 0.3)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.2',
                flexShrink: 0,
              }}
              onClick={() => handleQuickSet('1-month')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface DatePickerProps {
  value?: string | Dayjs | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  format?: string;
  disabled?: boolean;
  disabledDate?: (current: Dayjs) => boolean;
  style?: React.CSSProperties;
  size?: 'small' | 'middle' | 'large';
  allowClear?: boolean;
  className?: string;
}

export const DatePickerComponent: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  format = 'YYYY-MM-DD',
  disabled = false,
  disabledDate,
  style,
  size = 'middle',
  allowClear = true,
  className,
}) => {
  // Parse the value to Dayjs object
  const getDayjsValue = (): Dayjs | undefined => {
    if (!value) return undefined;

    let dayjsDate: Dayjs;

    // If it's already a Dayjs object
    if (dayjs.isDayjs(value)) {
      dayjsDate = value;
    } else {
      // If it's a string, parse it
      dayjsDate = dayjs(value);
    }

    // Return Dayjs object if valid, otherwise undefined
    return dayjsDate.isValid() ? dayjsDate : undefined;
  };

  const handleChange = (dayjsDate: Dayjs | null) => {
    if (onChange) {
      onChange(dayjsDate ? dayjsDate.toISOString() : null);
    }
  };

  const defaultDisabledDate = (current: Dayjs) => {
    return current && current.isBefore(dayjs().startOf('day'));
  };

  const datePickerProps = {
    value: getDayjsValue(),
    onChange: handleChange,
    placeholder,
    format,
    disabled,
    disabledDate: disabledDate || defaultDisabledDate,
    style,
    size,
    allowClear,
    className,
  };

  return <DayjsDatePicker {...datePickerProps} />;
};

// Form Item wrapper for Ant Design Form integration
export const DateTimeFormItem: React.FC<{
  name: string;
  label?: string;
  required?: boolean;
  rules?: any[];
  value?: string | Dayjs | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  showTime?: boolean;
  format?: string;
  disabledDate?: (current: Dayjs) => boolean;
  minuteStep?: number;
  showNow?: boolean;
  showToday?: boolean;
  showShortcuts?: boolean;
}> = ({
  name,
  label,
  required = false,
  rules = [],
  value,
  onChange,
  placeholder,
  disabled,
  size,
  style,
  className,
  showTime,
  format,
  disabledDate,
  minuteStep,
  showNow,
  showToday,
  showShortcuts,
}) => {
  const validationRules = [
    ...rules,
    {
      required,
      message: `${label || 'Date'} is required`,
    },
  ];

  return (
    <DateTimePickerField
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      size={size}
      style={style}
      className={className}
      showTime={showTime}
      format={format}
      disabledDate={disabledDate}
      minuteStep={minuteStep}
      showNow={showNow}
      showToday={showToday}
      showShortcuts={showShortcuts}
    />
  );
};
